import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Container,
  Form,
  Button,
  Row,
  Col,
  Alert,
  ListGroup,
  Badge,
  Modal,
} from "react-bootstrap";
import DoctorNavbar from "./DoctorNavbar";
import DoctorSidebar from "./DoctorSidebar";

const DoctorProfilePage = () => {
  const doctor = JSON.parse(localStorage.getItem("doctor"));
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [slotToDelete, setSlotToDelete] = useState(null);

  // Memoize fetchSlots so it doesn't change every render
  const fetchSlots = useCallback(async () => {
    try {
      const res = await axios.get(
        `https://ricgop.online/api/get-doctor-slots.php?doctorId=${doctor.id}`
      );
      setAvailableSlots(res.data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch slots.");
    }
  }, [doctor.id]);

  const getDayName = (dateString) => {
    const days = [
      "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
    ];
    const date = new Date(dateString);
    if (isNaN(date)) return "";
    return days[date.getDay()];
  };

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday as start
    return new Date(d.setDate(diff));
  };

  const getWeekRange = (date) => {
    const start = getWeekStart(date);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  const groupSlotsByWeek = (slots) => {
    const grouped = {};

    slots.sort((a, b) => new Date(a.date) - new Date(b.date));

    for (const slot of slots) {
      const weekKey = getWeekRange(slot.date);
      if (!grouped[weekKey]) grouped[weekKey] = [];
      grouped[weekKey].push(slot);
    }

    return grouped;
  };

  const handleAddSlot = async () => {
    setMessage("");
    setError("");

    if (!selectedDate || !selectedTime) {
      setError("Please select both date and time.");
      return;
    }

    const duplicate = availableSlots.some(
      (slot) => slot.date === selectedDate && slot.time === selectedTime
    );
    if (duplicate) {
      setError("This slot already exists.");
      return;
    }

    try {
      const res = await axios.post(
        "https://ricgop.online/api/save-doctor-slots.php",
        {
          doctorId: doctor.id,
          date: selectedDate,
          time: selectedTime,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (res.data.success) {
        setMessage("Slot added successfully!");
        setSelectedDate("");
        setSelectedTime("");
        fetchSlots();
      } else {
        setError(res.data.message || "Failed to add slot.");
      }
    } catch (err) {
      console.error(err);
      setError("Network or server error. Make sure CORS is configured on your backend.");
    }
  };

  // Show modal when user clicks delete button
  const confirmDeleteSlot = (slotId) => {
    setSlotToDelete(slotId);
    setShowDeleteModal(true);
  };

  // Delete after user confirms in modal
  const handleDeleteConfirmed = async () => {
    if (!slotToDelete) return;
    try {
      const res = await axios.post(
        "https://ricgop.online/api/delete-doctor-slot.php",
        { slotId: slotToDelete },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (res.data.success) {
        setMessage("Slot deleted successfully.");
        fetchSlots();
      } else {
        setError(res.data.message || "Failed to delete slot.");
      }
    } catch (err) {
      console.error(err);
      setError("Error deleting slot. Check your backend.");
    } finally {
      setShowDeleteModal(false);
      setSlotToDelete(null);
    }
  };

  // Include fetchSlots in useEffect dependencies since it is memoized now
  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  const weeklyGroupedSlots = groupSlotsByWeek(availableSlots);

  return (
    <>
      <div className="d-flex min-vh-100 bg-light">
        <DoctorSidebar />
        <main className="flex-grow-1">
          <DoctorNavbar doctor={doctor} />
          <Container className="my-5">
            <h3 className="text-primary fw-bold mb-4">Manage Slots</h3>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Select Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Select Time Slot</Form.Label>
                  <Form.Control
                    as="select"
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                  >
                    <option value="">-- Select Time --</option>
                    {Array.from({ length: 96 }).map((_, i) => {
                      const hour = Math.floor(i / 4).toString().padStart(2, "0");
                      const minute = (i % 4) * 15;
                      const time = `${hour}:${minute.toString().padStart(2, "0")}`;
                      return (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      );
                    })}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>

            <Button onClick={handleAddSlot} className="mb-3">
              Add Time Slot
            </Button>

            {message && <Alert variant="success">{message}</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}

            <h5 className="mt-4">Your Slots</h5>

            {Object.keys(weeklyGroupedSlots).length === 0 && (
              <ListGroup.Item>No slots available</ListGroup.Item>
            )}

            {Object.entries(weeklyGroupedSlots).map(([week, slots], i) => (
              <div key={i} className="mb-4">
                <h6 className="fw-bold text-secondary mb-2">Week: {week}</h6>
                <ListGroup>
                  {slots.map((slot, index) => (
                    <ListGroup.Item
                      key={index}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <strong>{getDayName(slot.date)}</strong>, {slot.date} at{" "}
                        {slot.time}
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <Badge
                          pill
                          bg={
                            slot.is_booked === "0" || slot.is_booked === 0
                              ? "success"
                              : "danger"
                          }
                        >
                          {slot.is_booked === "0" || slot.is_booked === 0
                            ? "Available"
                            : "Booked"}
                        </Badge>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => confirmDeleteSlot(slot.id)}
                          disabled={slot.is_booked === "1" || slot.is_booked === 1}
                        >
                          Delete
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            ))}
          </Container>
        </main>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        size="sm"
        dialogClassName="custom-delete-modal"
        backdrop="static"
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="text-danger fw-bold w-100 text-center">
            Delete Slot?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <p className="text-muted">
            Are you sure you want to delete this time slot?
          </p>
          <p className="text-muted small">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center border-0">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={handleDeleteConfirmed}>
            Yes, Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Inline CSS */}
      <style>{`
        .custom-delete-modal .modal-content {
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
          background: #fff;
          width:25%;
           position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: white;
            padding: 2rem;
            border-radius: 10px;
            z-index: 9999;
        }

        .custom-delete-modal .modal-header,
        .custom-delete-modal .modal-footer {
          border: none;
        }

        .custom-delete-modal .modal-title {
          font-size: 1.2rem;
        }
      `}</style>
    </>
  );
};

export default DoctorProfilePage;
